import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TablePagination,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";

/* ==============================
   Helpers
============================== */
function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return "--";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatWeek(weekStart) {
  if (!weekStart) return "—";
  try {
    const d = new Date(weekStart);
    const end = new Date(d);
    end.setDate(d.getDate() + 6);
    return `${d.toDateString()} – ${end.toDateString()}`;
  } catch {
    return weekStart;
  }
}

export default function JambHistoryPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  // Server-side pagination state
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10); // limit

  const offset = useMemo(() => page * rowsPerPage, [page, rowsPerPage]);

  const loadHistory = async (limit, off) => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get("/api/jamb/history", {
        params: { limit, offset: off },
      });

      setHistory(res.data?.history || []);
      setTotal(Number(res.data?.total || 0));
    } catch (e) {
      setError("Unable to load your JAMB history at this time.");
      setHistory([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory(rowsPerPage, offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, offset]);

  // Simple summary (optional but helpful)
  const bestScore = useMemo(() => {
    if (!history.length) return null;
    return Math.max(...history.map((h) => Number(h.score || 0)));
  }, [history]);

  if (loading) {
    return (
      <Box mt={6} display="flex" justifyContent="center">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth={1000} mx="auto" mt={4} p={2}>
        <Alert severity="error">{error}</Alert>
        <Box mt={2}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxWidth={1100} mx="auto" p={2}>
      {/* ================= Header ================= */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            📜 My JAMB Attempt History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track your weekly progress (attempt-level only — no per-subject data).
          </Typography>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            onClick={() => navigate("/dashboard/jamb/jamb-learderboard")}
          >
            🏆 Leaderboard
          </Button>

          <Button variant="contained" onClick={() => loadHistory(rowsPerPage, offset)}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* ================= Summary ================= */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography fontWeight={700} gutterBottom>
          📊 Summary
        </Typography>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Chip label={`Total Attempts: ${total}`} />
          <Chip
            label={`Best Score (this page): ${
              bestScore !== null ? `${bestScore} / 400` : "—"
            }`}
          />
          <Chip label={`Showing: ${history.length}`} />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Note: Per-subject score analysis remains available only for the current week (as designed).
        </Typography>
      </Paper>

      {/* ================= Table ================= */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Week</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Accuracy</TableCell>
                <TableCell>Rank</TableCell>
                <TableCell>Time Used</TableCell>
                <TableCell>Completed At</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      No completed JAMB attempts found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                history.map((h) => (
                  <TableRow key={h.week_start}>
                    <TableCell>{formatWeek(h.week_start)}</TableCell>
                    <TableCell>{Number(h.score ?? 0)} / 400</TableCell>
                    <TableCell>{h.accuracy}%</TableCell>
                    <TableCell>#{h.rank}</TableCell>
                    <TableCell>{formatTime(h.time_used)}</TableCell>
                    <TableCell>{formatDate(h.completed_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ================= Pagination ================= */}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const v = Number(e.target.value);
            setRowsPerPage(v);
            setPage(0); // reset to first page
          }}
          rowsPerPageOptions={[5, 10, 20, 50]}
        />
      </Paper>
    </Box>
  );
}
